<template>
  <el-row class="header">
    <el-col class="header__container">
      <i
        v-if="currentMenuItem && currentMenuItem.icon"
        class="header__icon"
        :class="currentMenuItem.icon"
      />
      <app-i18n
        v-if="currentMenuItem && currentMenuItem.codeLanguage"
        tag="h2"
        :code="currentMenuItem.codeLanguage"
        class="header__title"
      />
      <el-dropdown
        class="header__user"
        @command="handleDropdownCommand"
        :show-timeout="150"
        :hide-timeout="100"
      >
        <span class="el-dropdown-link">
          <div class="el-dropdown-link-body">
            <span
              class="header__user__name"
              v-if="!isMobile"
              v-text="currentUserNameOrEmailPrefix"
            />
            <div
              class="header__user__avatar"
              :style="{ backgroundImage: userAvatarUrl }"
            />
          </div>
        </span>
        <el-dropdown-menu
          class="header__user__menu"
          :visible-arrow="false"
          slot="dropdown"
        >
          <el-dropdown-item
            class="header__user__menu__option"
            command="doEditProfile"
          >
            <i class="icon-player" />
            <app-i18n code="auth.profile.title" />
          </el-dropdown-item>
          <el-dropdown-item
            class="header__user__menu__option"
            command="doSignout"
          >
            <i class="icon-sign-out" />
            <app-i18n code="auth.signout" />
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    
      <app-i18n-select class="header__i18n hide-xs" />
    </el-col>
    <el-col
      v-if="tabs && !!tabs.length"
      class="header__tabs"
    >
      <el-button-group>
        <el-button
          v-for="(tab, i) in tabs"
          :key="`header__tabs__tab--${i}`"
          class="header__tabs__tab"
          :class="{ 'header__tabs__tab--active': i === activeTabIndex }"
          v-text="tab.label"
          @click="onTabClick(tab)"
        />
      </el-button-group> 
    </el-col>
    <el-col
      v-if="subtabs && !!subtabs.length"
      class="header__subtabs"
      align="center"
    >
      <el-button-group>
        <el-button
          v-for="(tab, i) in subtabs"
          :key="`header__subtabs__tab--${i}`"
          class="header__subtabs__tab"
          :class="{ 'header__tabs__tab--active': i === activeSubtabIndex }"
          v-text="tab.label"
          @click="onSubtabClick(tab)"
        />
      </el-button-group> 
    </el-col>
  </el-row>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { i18n } from '@shared/i18n';

export default {
  name: 'app-header',
  computed: {
    ...mapGetters({
      currentMenuItem: 'layout/menu/currentMenuItem',
      currentUserNameOrEmailPrefix: 'auth/currentUserNameOrEmailPrefix',
      currentUserAvatar: 'auth/currentUserAvatar',
      isMobile: 'layout/isMobile',
      maxManage: 'auth/manage/maxManage',
      currentManage: 'auth/manage/currentManage',
      tabs: 'layout/headerTabs/tabs',
      subtabs: 'layout/headerTabs/subtabs',
      activeTabIndex: 'layout/headerTabs/activeTabIndex',
      activeSubtabIndex: 'layout/headerTabs/activeSubtabIndex',
    }),
    userAvatarUrl() {
      return this.currentUserAvatar ? `url(${this.currentUserAvatar})` : null;
    }
  },

  methods: {
    ...mapActions({
      doSignout: 'auth/doSignout',
      doBackManage: 'auth/manage/doBackManage',
      doResetTabs: 'layout/headerTabs/doReset',
      setActiveTabIndex: 'layout/headerTabs/setActiveTabIndex',
      setActiveSubtabIndex: 'layout/headerTabs/setActiveSubtabIndex',
    }),

    i18n(key, args) {
      return i18n(key, args);
    },

    handleDropdownCommand(command) {
      if (command === 'doSignout') this.doSignout({navigateOnSubmit: true});
      if (command === 'doEditProfile') this.doEditProfile();
    },

    doEditProfile() {
      return this.$router.push('/auth/edit-profile');
    },

    onTabClick({ index, routerPush }) {
      this.setActiveTabIndex(index);
      if (routerPush) this.$router.push(routerPush);
    },

    onSubtabClick({ index, routerPush }) {
      this.setActiveSubtabIndex(index);
      if (routerPush) this.$router.push(routerPush);
    },
  },  
  watch: {
    '$route.name'() {
      this.doResetTabs()
    },
  },
};
</script>

<style>
:root {
  --layout-header-height: 80px;
}
</style>

<style scoped>
.header {
  height: 100%;
}

.header__container {
  height: var(--layout-header-height);
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 28px 1fr 28px auto auto;
  grid-template-areas: 'icon title . user i18n';
  align-items: center;
  padding: 0 30px 0 40px;
  grid-gap: 12px;
}

.header__icon {
  grid-area: icon;
  font-size: 24px;
  color: #4c4c4c;
}

.header__title {
  grid-area: title;
  font-size: 22px;
  color: #4c4c4c;
  font-weight: 300;
  text-transform: uppercase;
}

.header__user {
  height: 60px;
  grid-area: user;
}

.header__user__name {
  font-size: 14px;
  font-weight: 500;
  color: #404040;
  margin-right: 15px;
}

.header__user__avatar {
  width: 48px;
  height: 48px;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
  border-radius: 100%;
}

.header__user__menu__option {
  font-size: 14px;
  font-weight: 500;
  color: #404040;
  opacity: 0.8;

  display: grid;
  grid-auto-flow: column;
  align-items: center;
  grid-template-columns: 20px 1fr;
  grid-gap: 5px;
}

.header__user__menu__option.el-dropdown-menu__item:focus,
.header__user__menu__option.el-dropdown-menu__item:not(.is-disabled):hover {
  background: #ffffff;
  color: #404040;
  opacity: 1;
}

.header__user__menu__option i {
  font-size: 16px;
  margin: 0;
}

.header__i18n {
  grid-area: i18n;
  margin: 0;
  height: 60px;
}

.header__tabs,
.header__subtabs {
  border-bottom: 1px solid rgba(197, 197, 197, 0.2);
  padding-bottom: 82px;
}

.header__tabs,
.header__tabs .el-button-group,
.header__tabs .el-button-group .el-button,
.header__subtabs,
.header__subtabs .el-button-group,
.header__subtabs .el-button-group .el-button {
  height: 82px;
  border-radius: 0;
  text-transform: initial;
}

.header__tabs__tab,
.header__subtabs__tab {
  min-width: 130px;
  font-size: 18px;
  letter-spacing: -0.4px;
  text-align: center;
  color: #404040;
  border: 0;
  border-bottom: 3px solid transparent;
  transition: color .3s ease-in;
  transition: border-color .5s ease-in;
  background-color: transparent;
}

.header__tabs__tab:active,
.header__tabs__tab:visited,
.header__tabs__tab:focus,
.header__subtabs__tab:active,
.header__subtabs__tab:visited,
.header__subtabs__tab:focus {
  background-color: transparent;
  color: #2acccc;
  border-color: #2acccc;
}

.header__tabs__tab:hover,
.header__subtabs__tab:hover {
  background-color: transparent;
  color: #2acccc;
  border-bottom: 3px solid transparent;
}

.header__tabs__tab--active,
.header__subtabs__tab--active,
.header__tabs__tab--active:hover,
.header__subtabs__tab--active:hover {
  color: #2acccc;
  border-color: #2acccc;
}
</style>
